import React, { FC, useEffect } from "react";
import { gql, useMutation } from "@apollo/client";

const REFRESH_STRIPE_DATA = gql`
  mutation RefreshParkerStripeData($parkerId: UUID!) {
    refreshParkerStripeData(parkerId: $parkerId) {
      id
      stripeCustomerId
      stripeMetadata
      stripeDefaultPaymentMethodId
    }
  }
`;

type StripeMetadataItem = {
  Id: string;
  Card: {
    Brand: string;
    Last4: string;
    ExpMonth: number;
    ExpYear: number;
  };
};

export const Payment: FC<{
  parker: {
    id: string;
    stripeCustomerId?: string | null;
    stripeMetadata?: string | null;
    stripeDefaultPaymentMethodId?: string | null;
  };
}> = ({ parker }) => {
  const [refreshStripeData, { loading: refreshing }] = useMutation(
    REFRESH_STRIPE_DATA,
    {
      variables: { parkerId: parker.id },
      // Update the cache with the refreshed data
      update: (cache, { data }) => {
        if (data?.refreshParkerStripeData) {
          cache.modify({
            id: cache.identify({ __typename: "Parker", id: parker.id }),
            fields: {
              stripeMetadata: () => data.refreshParkerStripeData.stripeMetadata,
              stripeDefaultPaymentMethodId: () => data.refreshParkerStripeData.stripeDefaultPaymentMethodId,
            },
          });
        }
      },
    }
  );

  // Refresh Stripe data when component mounts
  useEffect(() => {
    if (parker.stripeCustomerId) {
      refreshStripeData();
    }
  }, [parker.id, parker.stripeCustomerId, refreshStripeData]);

  const { stripeCustomerId, stripeMetadata, stripeDefaultPaymentMethodId } = parker;
  const metadata = stripeMetadata
    ? (JSON.parse(stripeMetadata) as StripeMetadataItem[])
    : [];

  return (
    <>
      {!stripeCustomerId && (
        <div className="alert alert-warning">
          This parker does not have a payment method setup.
        </div>
      )}

      {stripeCustomerId && (
        <>
          <div className="row mb-3">
            <div className="col-md">
              <label className="form-label">Stripe Customer Id</label>
              <div className="input-group">
                <input
                  type="text"
                  defaultValue={stripeCustomerId}
                  className="form-control"
                  disabled
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => refreshStripeData()}
                  disabled={refreshing}
                >
                  {refreshing ? "Refreshing..." : "Refresh"}
                </button>
              </div>
            </div>
          </div>

          {refreshing && (
            <div className="text-muted mb-2">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Loading payment methods from Stripe...
            </div>
          )}

          <table className="table">
            <thead>
              <tr>
                <th>Id</th>
                <th>Card</th>
                <th>Last 4</th>
                <th>Exp</th>
              </tr>
            </thead>
            <tbody>
              {metadata.map((item, index) => (
                <tr key={index}>
                  <td>{item.Id}</td>
                  <td>{item.Card.Brand}</td>
                  <td>{item.Card.Last4}</td>
                  <td>
                    {item.Card.ExpMonth + ` ` + item.Card.ExpYear}
                    {item.Id === stripeDefaultPaymentMethodId && (
                      <span className="badge bg-secondary ms-3">Default</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
};
